import { useState } from 'react';

import { useHandoffTargets, useInitiateHandoff } from '../hooks/use-handoffs';

import { CapabilityGate } from './CapabilityGate';

/**
 * HandoffInitiateModal — Fatia G + Item 293.
 *
 * Modo push: escolhe um atendente num `<select>` populado por
 * `useHandoffTargets` (nomes, não userId cru). Modo pull: digita a fila.
 * Estilo no padrão DS (tokens), gated por `peer-coordination-handoff`.
 */
export interface HandoffInitiateModalProps {
  conversationId: string;
  open: boolean;
  onClose: () => void;
}

const inputStyle = {
  background: 'var(--bg-surface-2)',
  borderColor: 'var(--border-default)',
};

export function HandoffInitiateModal({
  conversationId,
  open,
  onClose,
}: HandoffInitiateModalProps): JSX.Element | null {
  const initiate = useInitiateHandoff();
  const targets = useHandoffTargets();
  // `team` é modo da UI (A-32, noturna 04/Set/2026): no contrato vira `pull` +
  // `targetTeamId` — a fila do time, que o engine já aceitava sem a UI oferecer.
  // Os times vêm de `/handoffs/targets` (gate do handoff), não da Configuração:
  // o atendente não tem `team-config-read` e é ele quem transfere.
  const [mode, setMode] = useState<'push' | 'team' | 'pull'>('push');
  const [targetUserId, setTargetUserId] = useState('');
  const [targetTeamId, setTargetTeamId] = useState('');
  const [targetQueue, setTargetQueue] = useState('');
  const [reason, setReason] = useState('');
  const [contextSummary, setContextSummary] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'urgent'>('normal');

  if (!open) return null;

  const handleSubmit = (): void => {
    initiate.mutate(
      {
        conversationId,
        mode: mode === 'push' ? 'push' : 'pull',
        ...(mode === 'push' && { targetUserId }),
        ...(mode === 'team' && { targetTeamId }),
        ...(mode === 'pull' && { targetQueue }),
        reason,
        contextSummary,
        priority,
      },
      {
        onSuccess: () => {
          onClose();
          setTargetUserId('');
          setTargetTeamId('');
          setTargetQueue('');
          setReason('');
          setContextSummary('');
          setPriority('normal');
        },
      },
    );
  };

  const canSubmit =
    !initiate.isPending &&
    reason.trim().length >= 3 &&
    contextSummary.trim().length >= 3 &&
    (mode === 'push'
      ? targetUserId.length > 0
      : mode === 'team'
        ? targetTeamId.length > 0
        : targetQueue.trim().length >= 2);

  return (
    <CapabilityGate capability="peer-coordination-handoff">
      <div
        role="dialog"
        aria-label="Transferir conversa"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-lg rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-4 text-lg font-semibold">Transferir conversa</h2>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Modo</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={mode}
              onChange={(e): void => setMode(e.target.value as 'push' | 'team' | 'pull')}
              disabled={initiate.isPending}
            >
              <option value="team">Para um time — entra na fila do time</option>
              <option value="push">Para uma pessoa — ela aceita ou recusa</option>
              <option value="pull">Para uma fila por nome</option>
            </select>
          </label>

          {mode === 'team' && (
            <label className="mb-3 block">
              <span className="mb-1 block text-sm font-medium">Time</span>
              <select
                className="w-full rounded border px-2 py-1"
                style={inputStyle}
                value={targetTeamId}
                onChange={(e): void => setTargetTeamId(e.target.value)}
                disabled={initiate.isPending || targets.isLoading}
              >
                <option value="">{targets.isLoading ? 'Carregando…' : 'Selecione um time'}</option>
                {targets.teams.map((t) => (
                  <option key={t.teamId} value={t.teamId}>
                    {t.name}
                  </option>
                ))}
              </select>
              {!targets.isLoading && targets.teams.length === 0 && (
                <span className="mt-1 block text-xs text-text-secondary">
                  Este tenant ainda não tem times ativos — use «Para uma pessoa».
                </span>
              )}
            </label>
          )}

          {mode === 'push' && (
            <label className="mb-3 block">
              <span className="mb-1 block text-sm font-medium">Atendente</span>
              <select
                className="w-full rounded border px-2 py-1"
                style={inputStyle}
                value={targetUserId}
                onChange={(e): void => setTargetUserId(e.target.value)}
                disabled={initiate.isPending || targets.isLoading}
              >
                <option value="">
                  {targets.isLoading ? 'Carregando…' : 'Selecione um atendente'}
                </option>
                {targets.data.map((t) => (
                  <option key={t.userId} value={t.userId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {mode === 'pull' && (
            <label className="mb-3 block">
              <span className="mb-1 block text-sm font-medium">Fila (skill/setor)</span>
              <input
                className="w-full rounded border px-2 py-1"
                style={inputStyle}
                value={targetQueue}
                onChange={(e): void => setTargetQueue(e.target.value)}
                placeholder="ex.: tecnico, comercial"
                disabled={initiate.isPending}
                minLength={2}
                maxLength={80}
              />
            </label>
          )}

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Motivo</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              placeholder="Motivo da transferência (ex.: assunto fora da minha área)"
              disabled={initiate.isPending}
              minLength={3}
              maxLength={500}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Resumo de contexto</span>
            <textarea
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={contextSummary}
              onChange={(e): void => setContextSummary(e.target.value)}
              placeholder="Resumo do contexto para quem recebe: o que o cliente pediu e o que já foi feito"
              rows={3}
              disabled={initiate.isPending}
              minLength={3}
              maxLength={4000}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Prioridade</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={priority}
              onChange={(e): void => setPriority(e.target.value as 'low' | 'normal' | 'urgent')}
              disabled={initiate.isPending}
            >
              <option value="low">Baixa</option>
              <option value="normal">Normal</option>
              <option value="urgent">Urgente</option>
            </select>
          </label>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={initiate.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {initiate.isPending ? 'Enviando…' : 'Transferir'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
